import {
  Component,
  ElementRef,
  viewChild,
  inject,
  AfterViewChecked,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';

import { ChatService } from '../../../services/chat.service';
import { ChatDailyReportService } from '../../../services/chat-daily-report.service';
import { ComponentService } from '../../../services/component.service';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { ChatDailyReportCardComponent } from '../chat-daily-report-card/chat-daily-report-card.component';
import { ChatState } from '../../../types/Chatbot';
import { ChatReportDialogComponent } from '../chat-report-dialog/chat-report-dialog.component';
import { MessageService } from '../../../services/message.service';

@UntilDestroy()
@Component({
  selector: 'app-chat-panel',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
    TranslateModule,
    ChatMessageComponent,
    ChatDailyReportCardComponent,
  ],
  templateUrl: './chat-panel.component.html',
  styleUrl: './chat-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatPanelComponent implements AfterViewChecked, OnDestroy {
  public readonly closeRequested = output<void>();

  private readonly messagesContainer = viewChild<ElementRef>('messagesContainer');
  private readonly inputField = viewChild<ElementRef>('inputField');

  public readonly chatService = inject(ChatService);
  private readonly dailyReportService = inject(ChatDailyReportService);
  public readonly isDailyReportActive = inject(ComponentService).isFeatureActive('CHATBOT_DAILY_REPORT');
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  public state: ChatState = this.chatService.currentState;
  public readonly dailyReportState = this.dailyReportService.state;
  public inputValue = '';
  public readonly maxInputLength = 4000;
  public readonly warningThreshold = 0.9; // Show warning at 90% of max
  private shouldScrollToBottom = false;
  private scrollRAFId: number | null = null;
  private isDestroyed = false;
  public suggestions: string[] = [];

  public constructor() {
    this.chatService.state$.pipe(untilDestroyed(this)).subscribe((state) => {
      this.state = state;
      this.shouldScrollToBottom = true;
      this.cdr.markForCheck();
    });
    if (this.isDailyReportActive) {
      this.dailyReportService.initialize();
    }
    this.loadSuggestions();
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.loadSuggestions();
    });
  }

  private loadSuggestions(): void {
    const suggestionsObj = this.translateService.instant('chatbot.suggestions');
    // Check if translations exist - if missing, instant() returns the key string or object with untranslated keys
    if (typeof suggestionsObj === 'string' || !suggestionsObj) {
      this.suggestions = [];
      return;
    }
    const values = Object.values(suggestionsObj) as string[];
    // Filter out any values that look like untranslated keys (e.g., "chatbot.suggestions.1")
    this.suggestions = values.filter((value) => !value.startsWith('chatbot.suggestions'));
  }

  public ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom && !this.isDestroyed) {
      this.scheduleScrollToBottom();
    }
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.scrollRAFId !== null) {
      cancelAnimationFrame(this.scrollRAFId);
      this.scrollRAFId = null;
    }
  }

  public sendMessage(): void {
    if (this.inputValue.trim() && !this.state.isLoading) {
      this.chatService.sendMessage(this.inputValue);
      this.inputValue = '';
      const input = this.inputField();
      if (input) {
        input.nativeElement.focus();
        this.resetTextareaHeight();
      }
    }
  }

  public openReportProblemDialog(): void {
    const dialogRef = this.dialog.open(ChatReportDialogComponent, {
      width: '500px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter((details) => details !== undefined))
      .subscribe((details) => {
        this.chatService.reportProblem(details).subscribe({
          next: () => {
            this.messageService.showSuccessMessage('chatbot.report_problem_success');
          },
          error: () => {
            this.messageService.showErrorMessage('chatbot.report_problem_error');
          },
        });
      });
  }

  public adjustTextareaHeight(): void {
    const textarea = this.inputField();
    if (textarea) {
      const element = textarea.nativeElement;
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    }
  }

  private resetTextareaHeight(): void {
    const textarea = this.inputField();
    if (textarea) {
      textarea.nativeElement.style.height = 'auto';
    }
  }

  public sendSuggestion(suggestion: string): void {
    if (!this.state.isLoading) {
      this.chatService.sendMessage(suggestion);
    }
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  public async clearChat(): Promise<void> {
    await this.chatService.clearMessages();
  }

  public dismissPersistenceError(): void {
    this.chatService.clearPersistenceError();
  }

  public dismissDailyReport(): void {
    this.dailyReportService.dismiss();
  }

  public restoreDailyReport(): void {
    this.dailyReportService.restore();
  }

  public get isNearMaxLength(): boolean {
    return this.inputValue.length >= this.maxInputLength * this.warningThreshold;
  }

  public get isAtMaxLength(): boolean {
    return this.inputValue.length >= this.maxInputLength;
  }

  private scheduleScrollToBottom(): void {
    if (this.scrollRAFId !== null) {
      return; // Already scheduled
    }
    this.scrollRAFId = requestAnimationFrame(() => {
      this.scrollToBottom();
      this.scrollRAFId = null;
      this.shouldScrollToBottom = false;
    });
  }

  private scrollToBottom(): void {
    const container = this.messagesContainer();
    if (container) {
      container.nativeElement.scrollTop = container.nativeElement.scrollHeight;
    }
  }
}
